import { useEffect, useState } from "react";
import axios from "axios";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { Lock, Shield, CreditCard as CreditCardIcon, CheckCircle2, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * In-app card update modal for logged-in users (PAST_DUE / missing-sub
 * banner CTA). Uses the same proven token-backed endpoints as the public
 * `/billing/update-card/:token` page — mints a self-serve token via
 * `POST /api/billing/my-card-update-link`, then submits the card token to
 * `POST /api/billing/card-update/{token}`. Backend auto-creates the
 * recurring Subscription if the user is on a paid tier with no sub.
 *
 * On success, calls `onSuccess()` so the parent can refresh user state
 * and dismiss the banner.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - onSuccess: (responseData) => void
 *  - mode: 'past_due' | 'missing_sub'  (changes copy only)
 *  - tierLabel: string (e.g. 'Pro')
 */
export default function CardUpdateModal({ open, onOpenChange, onSuccess, mode = "missing_sub", tierLabel = "your" }) {
  const { toast } = useToast();
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) {
      setToken(null);
      setSuccess(null);
      setTokenError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const authToken = localStorage.getItem("token");

    Promise.all([
      axios.post(
        `${API_URL}/api/billing/my-card-update-link`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      ).then((r) => r.data),
      axios.get(`${API_URL}/api/payments/square-config`).then((r) => r.data),
    ])
      .then(([linkRes, cfgRes]) => {
        if (cancelled) return;
        setToken(linkRes?.token || null);
        setConfig(cfgRes);
      })
      .catch((err) => {
        if (cancelled) return;
        setTokenError(
          err.response?.data?.detail ||
            err.message ||
            "Couldn't load card form. Please refresh and try again."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleCardTokenized = async (tokenResult) => {
    if (tokenResult.status !== "OK") {
      toast({
        title: "Card validation failed",
        description: tokenResult.errors?.[0]?.detail || "Please check your card details.",
        variant: "destructive",
      });
      return;
    }
    if (!token) {
      toast({ title: "Session lost", description: "Please reopen this dialog.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/billing/card-update/${token}`, {
        source_id: tokenResult.token,
      });
      setSuccess(res.data);
      toast({
        title: "Card saved",
        description: res.data?.message || "You're all set.",
      });
      // Fire callback after a short pause so the user sees the success state
      setTimeout(() => {
        onSuccess?.(res.data);
      }, 1500);
    } catch (err) {
      toast({
        title: "Couldn't save your card",
        description:
          err.response?.data?.detail ||
          err.message ||
          "Please try a different card or contact support.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const headline = mode === "past_due" ? "Update your card" : `Add a card to ${tierLabel}`;
  const helper =
    mode === "past_due"
      ? "Your last payment was declined. Enter a new card to keep your subscription active — there'll be no charge today."
      : `We don't have a card on file for your ${tierLabel} subscription. Add one now — your next charge will be 30 days from today.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="card-update-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{headline}</DialogTitle>
          <DialogDescription>{helper}</DialogDescription>
        </DialogHeader>

        {/* Success state */}
        {success && (
          <div className="py-8 text-center" data-testid="card-update-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-900">Card saved</p>
            {success.card_brand && success.card_last4 && (
              <p className="text-sm text-slate-600 mt-1">
                {success.card_brand} ending {success.card_last4}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-3 max-w-xs mx-auto">{success.message}</p>
          </div>
        )}

        {/* Error state */}
        {!success && tokenError && (
          <div className="py-6 text-center text-sm text-rose-700" data-testid="card-update-token-error">
            {tokenError}
          </div>
        )}

        {/* Loading state */}
        {!success && !tokenError && loading && (
          <div className="h-32 flex items-center justify-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing secure card form…
          </div>
        )}

        {/* Form state */}
        {!success && !tokenError && !loading && config && token && (
          <>
            <div className="mt-2">
              <PaymentForm
                applicationId={config.application_id}
                locationId={config.location_id}
                cardTokenizeResponseReceived={handleCardTokenized}
              >
                <CreditCard
                  buttonProps={{
                    isLoading: submitting,
                    css: {
                      backgroundColor: "#0f172a",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#fff",
                      "&:hover": { backgroundColor: "#1e293b" },
                    },
                  }}
                >
                  {submitting ? "Saving…" : "Save card securely"}
                </CreditCard>
              </PaymentForm>
            </div>

            <div className="flex items-center justify-center gap-4 pt-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Encrypted</span>
              <span className="inline-flex items-center gap-1"><Shield className="h-3 w-3" /> PCI-DSS</span>
              <span className="inline-flex items-center gap-1"><CreditCardIcon className="h-3 w-3" /> Cancel any time</span>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed text-center pt-1">
              Card details are processed directly by Square. QuoteSorted never sees or stores your card number.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
